/**
 * ColorPicker — палитра утверждённых свотчей + свободный hex input.
 *
 * Решение D-2 (founder approved 2026-06-21): NOT picker нативный браузерный,
 * а контролируемая палитра, которой можно потом добавить «свотчи бренда».
 * Hex остаётся как fallback для нестандартных значений.
 */
import { useId } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DEFAULT_PALETTE = [
  '#F59E0B',
  '#22C55E',
  '#84CC16',
  '#0EA5E9',
  '#A855F7',
  '#EC4899',
  '#14B8A6',
  '#EF4444',
  '#FBBF24',
  '#9CA3AF',
];

const HEX_RE = /^#([0-9A-Fa-f]{6})$/;

export function isValidHex(v: string): boolean {
  return HEX_RE.test(v);
}

export interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  palette?: string[];
  disabled?: boolean;
}

export function ColorPicker({
  value,
  onChange,
  palette = DEFAULT_PALETTE,
  disabled,
}: ColorPickerProps) {
  const inputId = useId();
  const valid = isValidHex(value);

  return (
    <div className="flex items-center gap-3">
      <div
        className="h-9 w-9 shrink-0 rounded-md border border-border"
        style={{ backgroundColor: valid ? value : 'transparent' }}
        aria-label="Текущий цвет"
      />
      <div className="flex-1">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {palette.map((p) => {
            const selected = p.toLowerCase() === value.toLowerCase();
            return (
              <button
                key={p}
                type="button"
                disabled={disabled}
                onClick={() => onChange(p)}
                className={cn(
                  'h-6 w-6 rounded-md transition',
                  selected
                    ? 'ring-2 ring-foreground ring-offset-1'
                    : 'ring-0 hover:ring-1 hover:ring-border',
                )}
                style={{ backgroundColor: p }}
                aria-label={`Цвет ${p}`}
                aria-pressed={selected}
              />
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor={inputId} className="text-xs text-muted-foreground">
            HEX
          </Label>
          <Input
            id={inputId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={cn(
              'h-8 w-28 font-mono text-xs',
              !valid && 'border-destructive focus-visible:ring-destructive',
            )}
            placeholder="#9CA3AF"
            spellCheck={false}
          />
          {!valid && (
            <span className="text-[11px] text-destructive">формат #RRGGBB</span>
          )}
        </div>
      </div>
    </div>
  );
}
